import React, { useState, useEffect } from 'react';
import Breadcrumb from "app/components/Breadcrumb";
import shortid from "shortid";
import { Delete, Edit, Search } from "@mui/icons-material";
import useLoanOfficer from "./hooks/useLoanOfficer";
import LoanOfficerEditorDialog from "./components/LoanOfficerEditorDialog";
import ConfirmationDialog from "app/components/ConfirmationDialog";
import { themeShadows } from "app/components/MatxTheme/options/themeColors";
import {
    Box,
    Card,
    Table,
    styled,
    TableRow,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TextField,
    InputAdornment,
    Button,
    IconButton,
    Tooltip,
} from "@mui/material";
import useRoles from "app/hooks/useRoles";

const Status = styled("small")(({ theme }) => ({
    color: "#fff",
    padding: "2px 8px",
    overflow: "hidden",
    borderRadius: "300px",
    boxShadow: themeShadows[3],
    background: theme.palette.primary.main
}));

export default function LoanOfficer() {
    const [newLoanOfficer, setNewLoanOfficer] = useState({
        fieldid: '',
        description: '',
        type: ''
    });

    const getrolebyusername = useRoles();
    const isActionEnabled = getrolebyusername.includes('SA') || getrolebyusername.includes('Admin');

    const Container = styled("div")(({ theme }) => ({
        margin: "30px",
        [theme.breakpoints.down("sm")]: { margin: "16px" },
        "& .breadcrumb": {
            marginBottom: "0px",
            [theme.breakpoints.down("sm")]: { marginBottom: "16px" }
        }
    }));

    const LoanOfficerTable = styled(Table)(() => ({
        minWidth: 750,
        whiteSpace: "pre",
        "& thead": { "& th:first-of-type": { paddingLeft: 16 } },
        "& td": { borderBottom: "none" },
        "& td:first-of-type": { paddingLeft: "16px !important" }
    }));

    const handleAddNewUser = () => {
        setLoanOfficer(null);
        setShouldOpenEditorDialog(true);
        handleEditUser(null);
    };

    const {
        handleEditUser,
        handleDeleteUser,
        shouldOpenEditorDialog,
        handleDialogClose,
        page,
        rowsPerPage,
        setRowsPerPage,
        LoanOfficer,
        setShouldOpenEditorDialog,
        LoanOfficers,
        setLoanOfficer,
        handleChangePage,
        shouldOpenConfirmationDialog,
        handleConfirmationResponse
    } = useLoanOfficer();

    const [searchQuery, setSearchQuery] = useState("");
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };
    const filteredLoanOfficerList = LoanOfficers.filter(LoanOfficerrecord =>
        String(LoanOfficerrecord.code).startsWith(String(searchQuery)) ||
        LoanOfficerrecord.description.toLowerCase().startsWith(String(searchQuery).toLowerCase())
    );

    const handleInputChange = (e) => {
        setNewLoanOfficer({ ...newLoanOfficer, [e.target.name]: e.target.value });
    };

    const STATUSTYPES = {
        ACTIVE: 1,
        DEFAULT: 2,
        DELETED: 3,
    };

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb routeSegments={[{ name: "Loan Officer" }]} />
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    sx={{ mb: 2, mt: 2 }}
                    color="primary"
                    variant="contained"
                    onClick={handleAddNewUser}
                >
                    Add Loan Officer
                </Button>

                <TextField
                    label="Search by Code or Description"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '300px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        sx: { height: '40px' }
                    }}
                />
            </Box>
            <Card sx={{ width: "100%", overflow: "auto" }} elevation={6}>
                <LoanOfficerTable>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 100 }} >Code</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell > Status</TableCell>
                            {isActionEnabled && <TableCell>Action</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredLoanOfficerList && filteredLoanOfficerList.length > 0 && filteredLoanOfficerList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((LoanOfficer) => (
                            <TableRow hover key={shortid.generate()}>
                                <TableCell sx={{ px: 0 }} align="left">
                                    {LoanOfficer.code}
                                </TableCell>
                                <TableCell sx={{ px: 0 }} align="left">
                                    {LoanOfficer.description}
                                </TableCell>
                                <TableCell sx={{ px: 0 }} align="left">
                                    {LoanOfficer.statusLUId == STATUSTYPES.ACTIVE && <Status>Active</Status>}
                                    {LoanOfficer.statusLUId == STATUSTYPES.DEFAULT && <Status>Default</Status>}
                                    {LoanOfficer.statusLUId == STATUSTYPES.DELETED && <Status>Deleted</Status>}
                                </TableCell>
                                {isActionEnabled && (
                                    <TableCell sx={{ px: 0 }} align="left">
                                        <IconButton color="primary" onClick={handleEditUser(LoanOfficer)} disabled={LoanOfficer.statusLUId == STATUSTYPES.DEFAULT} >
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDeleteUser(LoanOfficer)} disabled={LoanOfficer.statusLUId == STATUSTYPES.DEFAULT}>
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </LoanOfficerTable>

                <TablePagination
                    page={page}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    count={filteredLoanOfficerList.length}
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={[10, 20, 30]}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
            </Card>

            {
                shouldOpenEditorDialog && (
                    <LoanOfficerEditorDialog
                        open={shouldOpenEditorDialog}
                        handleClose={handleDialogClose}
                        LoanOfficer={LoanOfficer}
                    />
                )
            }

            {
                shouldOpenConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to delete this Loan Officer?"
                    />
                )
            }
        </Container >

    );
}